import { yupResolver } from "@hookform/resolvers/yup";
import { ISelectOption, scheduling } from "lobbie";
import { Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import HelpTooltip from "src/components/shared/tooltips/HelpTooltip";
import Select, { SingleValue } from "react-select";
import { useLobbieAccountRedux } from "src/hooks/accounts/accounts";
import { isEmptyObject, logDev, notify } from "src/utils";
import { NONOPTIONAL_PATIENT_CHILD_VALIDATION_SCHEMA } from "src/utils/validations/patient";
import { REACT_SELECT_STYLING } from "src/constants/styling";
import { useCallback, useMemo } from "react";

interface IProps {
    existingChild?: scheduling.patient.IPatientDetailsChild;
    options: ISelectOption[];
    handleSavingNewChild: (child: scheduling.patient.IPatientDetailsChild) => void;
    handleCancel: () => void;
}

interface IFormValues {
    firstName: string;
    lastName: string;
    dateOfBirth?: string;
    accountPatientId?: number;
    parentRelationshipName: string;
    isEdited: boolean;
}

const PatientSchedulingUserDetailsChildForm: React.FC<IProps> = ({
    existingChild,
    options,
    handleSavingNewChild,
    handleCancel,
}) => {
    const { account } = useLobbieAccountRedux();
    const defaultValues = useMemo(() => {
        return {
            firstName: existingChild?.firstName ?? "",
            lastName: existingChild?.lastName ?? "",
            dateOfBirth: existingChild?.dateOfBirth,
            accountPatientId: existingChild?.accountPatientId,
            parentRelationshipName: existingChild?.parentRelationshipName ?? "",
            isEdited: existingChild?.isEdited ?? false,
        };
    }, [
        existingChild?.firstName,
        existingChild?.lastName,
        existingChild?.dateOfBirth,
        existingChild?.accountPatientId,
        existingChild?.isEdited,
        existingChild?.parentRelationshipName,
    ]);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<IFormValues>({
        // @ts-ignore
        resolver: yupResolver(NONOPTIONAL_PATIENT_CHILD_VALIDATION_SCHEMA),
        defaultValues,
    });

    const watchParentRelationshipName = watch("parentRelationshipName");

    const handleChangeParentRelationshipName = useCallback(
        (value: SingleValue<ISelectOption>) => {
            logDev("Change relation to -", value?.value);
            if (!value?.value) {
                setValue("parentRelationshipName", "");
            } else {
                setValue("parentRelationshipName", value.value as string);
            }
        },
        [setValue],
    );

    const onSubmit = useCallback(
        (data: IFormValues) => {
            if (!data.parentRelationshipName) {
                notify({
                    level: "warning",
                    title: "No relationship selected",
                });
                return;
            }
            handleSavingNewChild({
                firstName: data.firstName,
                lastName: data.lastName,
                dateOfBirth: data.dateOfBirth,
                isEdited: true,
                accountPatientId: existingChild?.accountPatientId,
                parentRelationshipName: data.parentRelationshipName,
                isSelected: existingChild?.isSelected || false,
            });
        },
        [existingChild?.accountPatientId, existingChild?.isSelected, handleSavingNewChild],
    );

    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="col border rounded p-3">
                <Form.Label className="bold mt-3">First Name*</Form.Label>
                <Form.Control
                    {...register("firstName")}
                    required={true}
                    type={"text"}
                    placeholder={"First Name"}
                    defaultValue={existingChild?.firstName}
                    isInvalid={!!(errors && errors["firstName"])}
                    autoComplete="given-name"
                />{" "}
                <Form.Label className="bold mt-3">Last Name*</Form.Label>
                <Form.Control
                    {...register("lastName")}
                    required={true}
                    type={"text"}
                    placeholder={"Last Name"}
                    defaultValue={existingChild?.lastName}
                    isInvalid={!!(errors && errors["lastName"])}
                    autoComplete="family-name"
                />
                {account.verifyDOBOnFormsOpen && (
                    <Form.Control
                        {...register("dateOfBirth")}
                        required={true}
                        type={"text"}
                        placeholder={"Date of Birth"}
                        defaultValue={existingChild?.dateOfBirth}
                        isInvalid={!!(errors && errors["dateOfBirth"])}
                        autoComplete="bday"
                    />
                )}
                <div className="row mt-3">
                    <div className="col px-3">
                        <span>
                            <HelpTooltip
                                tooltipId="newRepresentativeTooltip"
                                message={"Descriptor of your relationship."}
                                title={
                                    <div className="bold">{`How are you related to this
                                      ${(
                                          account.patientFieldName || "person"
                                      ).toLowerCase()}?*`}</div>
                                }
                            />
                        </span>
                        <Select
                            classNamePrefix="cy-react-select"
                            options={
                                isEmptyObject(options)
                                    ? ([{ value: "Other", label: "Other" }] as ISelectOption[])
                                    : options
                            }
                            placeholder={"Select a relationship type..."}
                            onChange={handleChangeParentRelationshipName}
                            value={
                                watchParentRelationshipName
                                    ? ({
                                          label: watchParentRelationshipName,
                                          value: watchParentRelationshipName,
                                      } as ISelectOption)
                                    : undefined
                            }
                            styles={REACT_SELECT_STYLING}
                        />
                    </div>
                </div>
                <div className="col my-3">
                    <div className="row">
                        <div className="col-6 pl-0">
                            <Button
                                className="bg-white blue semi-bold shadow-none w-100"
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                        </div>
                        <div className="col-6 pr-0">
                            <Button
                                className="primary white semi-bold shadow-none w-100"
                                type={"submit"}
                            >
                                Save&nbsp;{account.patientFieldName}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Form>
    );
};
export default PatientSchedulingUserDetailsChildForm;
